import { CFormCheck, CSpinner } from '@coreui/react'
import { QRCodeSVG } from 'qrcode.react'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import AppleWallet from 'src/assets/apple_wallet_de.svg'
import GPay from 'src/assets/google_wallet_de.svg'
import LogoPlaceholder from 'src/assets/logo_placeholder.png'
import { getMobileOperatingSystem } from 'src/helpers/deviceDetection'
import { useToast } from 'src/hooks/useToast'
import TermsAndConditions from 'src/views/pages/qrDownload/TermsAndConditions'
import { acceptTos, getAppleLink, getGoogleLink, getPageDescription, getPageLogo } from 'src/api/downloadPage'
import './QrDownload.scss'

const progressMessages = [
  'Your wallet pass is now being created...',
  'Please wait while we set things up...',
  'Almost there, finalizing details...',
  'Wrapping up the creation process...',
  'Preparing pass for download...',
]

const QRDownload = () => {
  const { toaster, pushToast } = useToast()
  const { serialNumber } = useParams()
  const [applePassLink, setApplePassLink] = useState('')
  const [googlePassLink, setGooglePassLink] = useState('')
  const [pageDescription, setPageDescription] = useState('')
  const [pageLogo, setPageLogo] = useState('')
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isFetchingLinks, setIsFetchingLinks] = useState(false)
  const [currentSystem, setCurrentSystem] = useState(null)
  const [isTosChecked, setIsTosChecked] = useState(false)
  const [isTosInvalid, setIsTosInvalid] = useState(false)
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState(progressMessages[0])

  const fetchPageDetails = async () => {
    await Promise.all([
      getPageLogo(serialNumber).then(setPageLogo).catch(() => {
        pushToast('Could not download page logo.', 'danger')
      }),
      getPageDescription(serialNumber).then(res => {
        if (res?.message === 'success') {
          setPageDescription(res?.description || null)
        }
      }),
    ])
  }

  useEffect(() => {
    if (isFetchingLinks) {
      setStatusMessage(progressMessages[Math.floor(progress / 20)])
    }
  }, [progress])

  const fetchPassLinks = async ({ appleOnly = false, googleOnly = false }) => {
    setIsFetchingLinks(true)
    setProgress(0)

    const fetchLinks = async () => {
      const MAX_TRIES = 20
      const TRY_DURATION = 4000
      let tries = 0

      while (tries < MAX_TRIES) {
        const results = await Promise.all([
          appleOnly && getAppleLink(serialNumber),
          googleOnly && getGoogleLink(serialNumber),
        ])

        const [appleRes, googleRes] = results

        if (appleRes?.message === 'success' && appleRes?.url) {
          if (appleOnly) {
            setProgress(99)
            setTimeout(() => window.location.assign(appleRes.url), 1500)
          } else {
            setApplePassLink(appleRes.url)
          }
        } else if (appleRes && !appleRes?.url && (appleRes.message !== 'pending' || tries >= MAX_TRIES - 1)) {
          pushToast('Apple pass link is missing. Please try again later.', 'danger')
        }

        if (googleRes?.message === 'success' && googleRes?.url) {
          if (googleOnly) {
            setProgress(99)
            setTimeout(() => window.location.assign(googleRes.url), 1500)
          } else {
            setGooglePassLink(googleRes.url)
          }
        } else if (googleRes && !googleRes?.url && (googleRes.message !== 'pending' || tries >= MAX_TRIES - 1)) {
          pushToast('Google pass link is missing. Please try again later.', 'danger')
        }

        if ((appleRes?.message === 'success' && appleRes?.url) || (googleRes?.message === 'success' && googleRes?.url)) {
          return
        }

        tries++
        setProgress(pv => ~~Math.min(tries * (100 / MAX_TRIES) + (Math.random() - 0.5), 100))
        if (tries < MAX_TRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, tries < MAX_TRIES / 2 ? TRY_DURATION : 2 * TRY_DURATION))
        }
      }
    }
    await fetchLinks().finally(() => {
      setTimeout(() => setIsFetchingLinks(false), 1500)
    })
  }

  useEffect(() => {
    setCurrentSystem(getMobileOperatingSystem())
    fetchPageDetails().finally(() => setIsPageLoading(false))
  }, [])

  useEffect(() => {
    if (!isTosChecked) {
      setApplePassLink('')
      setGooglePassLink('')
    }
  }, [isTosChecked])

  const handlePassDownload = (platform) => (evt) => {
    evt.preventDefault()
    if (isTosChecked) {
      if (platform === 'apple') {
        applePassLink ? window.location.assign(applePassLink) : fetchPassLinks({ appleOnly: true })
      }
      if (platform === 'google') {
        googlePassLink ? window.location.assign(googlePassLink) : fetchPassLinks({ googleOnly: true })
      }
    } else {
      setIsTosInvalid(!isTosChecked)
    }
  }

  const handleTosChange = (evt) => {
    const checked = evt.target.checked
    setIsTosChecked(checked)
    acceptTos(serialNumber, checked).then((res) => {
      if (res.message === 'success') {
        setIsTosInvalid(false)
      }
    }).catch(err => {
      setIsTosInvalid(true)
    })
  }

  const pageFooter = <form className="download-section">
    { (currentSystem === 'iOS' || currentSystem === 'Android') && <div className="policy-and-terms">
      <CFormCheck
        id="tosCheckDefault"
        label={ <TermsAndConditions /> }
        invalid={ isTosInvalid }
        feedbackInvalid="Dies zu akzeptieren ist erforderlich."
        onChange={ handleTosChange }
        style={ { width: 20, height: 20, marginTop: 1 } }
      />
    </div>
    }
    <div className="download-images pps-fade-in">
      { currentSystem === 'iOS' &&
        <div onClick={ handlePassDownload('apple') } className="d-grid align-items-center justify-content-center">
          {
            isFetchingLinks && <div style={ { gridArea: '1/1/1/1' } }
                                    className="mx-auto">
              <CSpinner component="div" aria-hidden="true" />
            </div>
          }
          <img
            className={ 'mx-2 ' + (isTosChecked ? '' : 'disabled') }
            src={ AppleWallet }
            alt="apple wallet"
            width={ 225 }
            key={ applePassLink }
            style={ { gridArea: '1/1/1/1', opacity: isFetchingLinks ? 0.15 : 1 } }
          />
        </div>
      }
      { currentSystem === 'Android' &&
        <div onClick={ handlePassDownload('google') }
             className="d-grid align-items-center justify-content-center">
          {
            isFetchingLinks && <div style={ { gridArea: '1/1/1/1' } }
                                    className="mx-auto">
              <CSpinner component="div" aria-hidden="true" />
            </div>
          }
          <img
            className={ 'mx-2 ' + (isTosChecked ? '' : 'disabled') }
            src={ GPay }
            alt="google pay"
            width={ 230 }
            key={ googlePassLink }
            style={ { gridArea: '1/1/1/1', opacity: isFetchingLinks ? 0.15 : 1 } }
          />
        </div>
      }
    </div>

    { currentSystem !== 'iOS' && currentSystem !== 'Android' && <div className="qr-code-container">
      <QRCodeSVG
        value={ window.location.href }
        size={ 200 }
        bgColor="transparent"
        includeMargin
      />
      <div>
        QR-Code mit Smartphone scannen.
      </div>
    </div>
    }
  </form>

  return (
    <>
      { toaster }
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100"
           style={ {
             background: 'linear-gradient(135deg, #ece9e6, #ffffff)',
             visibility: isFetchingLinks ? 'visible' : 'hidden',
             position: 'absolute',
             zIndex: 10,
             top: 0,
             width: '100vw',
             overflow: 'hidden',
           } }>
        <h2 style={ {
          fontFamily: '\'Roboto\', sans-serif',
          fontSize: '1.5em',
          marginBottom: '20px',
          textAlign: 'center',
          color: '#333',
        } }>Wallet Pass Erstellung</h2>

        {/* Loader */ }
        <div
          className="spinner-border"
          style={ {
            width: '80px',
            height: '80px',
            borderWidth: '10px',
            borderColor: 'rgba(0, 0, 0, 0.1)',
            borderTopColor: '#B9D8A5',
            borderRadius: '50%',
            marginBottom: '20px',
            // boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          } }
          role="status"
        ></div>

        {/* Progress Bar */ }
        <div
          className="progress"
          style={ {
            width: '90%',
            maxWidth: '400px',
            height: '20px',
            backgroundColor: '#ddd',
            borderRadius: '50px',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px',
            position: 'relative',
          } }
        >
          <div
            className="progress-bar"
            role="progressbar"
            style={ {
              width: `${ progress }%`,
              backgroundColor: '#B9D8A5',
              height: '100%',
              transition: 'width 0.2s ease',
            } }
          ></div>
          <div
            style={ {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'rgba(0,0,0,0.5)',
              fontSize: '0.9em',
              fontWeight: 'bold',
            } }
          >
            { progress }%
          </div>
        </div>

        {/* Status Text */ }
        <div style={ { fontSize: '1em', textAlign: 'center', color: '#555', marginTop: '10px' } }>
          { statusMessage }
        </div>
        <div style={ {
          fontSize: '1em',
          textAlign: 'center',
          color: 'rgba(0,0,0,0.5)',
          marginTop: '5px',
          fontWeight: 300,
        } }>
          Please hold for a moment, it will open automatically
        </div>
        <div style={ { fontSize: '0.9em', textAlign: 'center', color: '#777', marginTop: '5px' } }></div>
      </div>
      <div className="qr-download-page bg-light min-vh-100"
           style={ {
             position: 'relative',
             top: 0,
             width: '100%',
             backgroundColor: '#f0f0f0',
             display: 'flex',
             justifyContent: 'center',
             alignItems: 'center',
             padding: '20px',
           } }>
        {
          isPageLoading
          ? <div className="align-self-center">
            <CSpinner component="div" aria-hidden="true" />
          </div>
          : <div className="card" style={ {
            maxWidth: '420px',
            backgroundColor: '#fff',
            borderRadius: 12,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            padding: 25,
            textAlign: 'center',
          } }>
            { pageLogo && (
              <div className="header" style={ { margin: '0 auto' } }>
                <img
                  src={ pageLogo || LogoPlaceholder }
                  alt="Page Logo"
                  style={ { maxWidth: '220px', height: 'auto', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 5 } }
                />
              </div>
            ) }
            <div style={ {
              fontSize: '1.2rem',
              margin: '10px 0',
            } }>
              { pageDescription || 'Hier kannst Du deine neue digitale Kundenkarte zu Wallet hinzufügen.' }
            </div>
            { pageFooter }
          </div>
        }
      </div>
    </>
  )
}

export default QRDownload
